/*
 * Copyright (c) 2022 CalAmp Corp.  All Rights Reserved
 */
import React, {
  ChangeEvent,
  useCallback,
  useMemo
} from 'react';
import {makeStyles} from '@material-ui/core/styles';
import Autocomplete from '@material-ui/lab/Autocomplete';
import {translate} from '@ion/core/src/locale/locale';
import {DeviceFile, DownloadJobTasksElem} from '@ion/core/src/types/entities';
import {IonSpinner} from '@ion/dls/src/components/loading/IonSpinner';
import Tooltip from '@ion/dls/src/components/tooltip/Tooltip';
import {getAppId} from '@ion/dls/src/editors/DeviceFileEditorUtils';
import {WarningIcon} from '@ion/dls/src/icons/WarningIcon';
import Render from '@ion/dls/src/render/Render';
import {IconSize} from '@ion/dls/src/types/types';
import {useDeviceFiles} from '@ion/query/src/hooks/useDeviceFiles';
import {
  CONFIG,
  DEFAULT_DEVICE_TYPES,
  getFileWarningMessages,
  onRenderFileNameOption,
  onRenderInput
} from './helpers';
import {useJobTaskChooserStyles} from './useJobTaskChooserStyles';

// defines the file name chooser prop definitions ...
export type JobTaskFileNameChooserProps = {
  row: DownloadJobTasksElem;
  accountId: number;
  useSearchChooser?: boolean;
  includeSubAccounts: boolean;
  isDevicesSelected?: boolean;
  devicePlatformIds?: Array<Number>;
  onChange: (event: ChangeEvent, value: DeviceFile) => void;
  suppressNullVersions?: boolean;
}

// defines the styles used by the file name chooser ...
export const useStyles = makeStyles(() => ({
  spinner : {
    display        : 'flex',
    alignItems     : 'center',
    justifyContent : 'center',
    width          : '100%',
    height         : '100%'
  },
  dropdownIconWrapper : {
    display    : 'flex',
    alignItems : 'flex-end'
  },
  icon : {
    borderBottom : '1px solid #bdbdbd',
    paddingLeft  : '5px',
    cursor       : 'pointer',
    position     : 'absolute',
    zIndex       : 999
  },
  dropdownField : {
    flex                       : 1,
    '& .MuiAutocomplete-input' : {
      paddingLeft : '24px !important'
    }
  },
  dropdownInputText : {
    paddingLeft : '24px !important'
  },
  tooltipiList : {
    paddingLeft : 20,
    textAlign   : 'left'
  },
  tooltipiListItem : {
    paddingBottom : 5
  }
}));

/**
 * The JobTaskFileNameChooser provides a list of file names for the current account
 * and allows the user to select a file name from the list.
 *
 * ```typescript
 *
 *     import {JobTaskFileNameChooser} from './JobTaskFileNameChooser';
 *
 *     // with callback subscription ...
 *     <JobTaskFileNameChooser onChange={handleFileName(value, index, tasks)} />
 *
 * ```
 */
export const JobTaskFileNameChooser = ({
  row,
  accountId,
  includeSubAccounts,
  devicePlatformIds,
  onChange,
  isDevicesSelected = false,
  suppressNullVersions = false
}: JobTaskFileNameChooserProps) => {
  const classes             = useStyles();
  const {task}              = row;
  const {dropdownProps}     = useJobTaskChooserStyles();
  const onGetSelectedOption = useCallback((option, value) => {
    if (!option || !value) return false;
    return option?.name === value?.name || option?.name === value;
  }, []);
  const onGetOptionLabel = useCallback(fileName => fileName?.name ? fileName?.name : '', []);
  const usesAppIds       = task?.usesAppIds;
  const appIds           = task?.appId?.length ? [task.appId] : task.deviceAppIds;
  const isVbus           = DEFAULT_DEVICE_TYPES?.includes(task?.deviceType) && task?.fileType !== CONFIG;
  const warningMessage   = getFileWarningMessages(task);

  const {deviceFiles, isLoading} = useDeviceFiles({
    enabled           : !!task?.deviceType,
    devicePlatformIds : isVbus ? devicePlatformIds : [],
    platformIds       : [task?.platformId],
    enabledOnly       : true,
    ...(usesAppIds && {appIds}),
    suppressNullVersions,
    includeSubAccounts,
    accountId
  });

  const currentValue = task?.fileLabel ? {name: task.fileLabel} : null;

  const onBlur = useCallback((event) => {
    event.stopPropagation();
  }, []);

  const isFileTypeInvalid      = !task?.fileType;
  const isDeviceTypeInvalid    = !task?.deviceType;
  const isAutocompleteDisabled =
    !isDevicesSelected ||
    isDeviceTypeInvalid ||
    isFileTypeInvalid;

  return (
    isLoading ? (
      <div className={classes.spinner}>
        <IonSpinner width='25px' height='25px' />
      </div>
    ) : (
      <div className={classes.dropdownIconWrapper}>
        <Render.If condition={warningMessage?.length > 0}>
          <Tooltip arrow
            title={
              <Render>
                <Render.If condition={warningMessage?.length > 1}>
                  <ul className={classes.tooltipiList}>
                    {warningMessage?.map((message, index) => (
                      <li key={index} className={classes.tooltipiListItem}>
                        <span>{message}</span>
                      </li>
                    ))}
                  </ul>
                </Render.If>
                <Render.Else>
                  <span>{warningMessage[0]}</span>
                </Render.Else>
              </Render>
            }
            className={classes.icon}>
            <span><WarningIcon size={IconSize.small} /></span>
          </Tooltip>
        </Render.If>
        <div className={classes.dropdownField}>
          <Autocomplete disableClearable
            disabled={isAutocompleteDisabled}
            options={deviceFiles ?? []}
            value={currentValue}
            getOptionSelected={onGetSelectedOption}
            filterOptions={filterOptions}
            classes={dropdownProps}
            getOptionLabel={onGetOptionLabel}
            renderOption={onRenderFileNameOption}
            onChange={onChange}
            onBlur={onBlur}
            renderInput={onRenderInput(translate('jobcreate:fields.fileName.placeholder'))} />
        </div>
      </div>
    )
  );
};

/**
 * Filters options by comparing the current dropdown value with each option. If an option
 * contains the value in its name, file version, or app ID, it is included in the returned
 * list.
 */
const filterOptions = (options: DeviceFile[], {inputValue}: {inputValue: string}) => {
  if (inputValue === '') {
    return options;
  } else {
    const files = [];
    const value = inputValue.toLowerCase();
    options.forEach(option => {
      const name        = option?.name?.toLowerCase();
      const {fields}    = option?.validationMetaData;
      const fileVersion = (fields?.version)?.toString()?.toLowerCase();
      const appId       = getAppId(option)?.toLowerCase() || '';
      if (name?.includes(value) ||
        fileVersion?.includes(value) ||
        appId?.includes(value)) {
        files.push(option);
      }
    });
    return files;
  }
};

export default JobTaskFileNameChooser;
